/**
 * Utilities for manipulating prompt templates with variable placeholders.
 * Used for automatically inserting/removing custom input variables.
 */

/**
 * Insert a new variable placeholder into a prompt template.
 * Adds it at the end with proper formatting.
 * 
 * @param promptContent - Current prompt content
 * @param variableName - Variable name to insert (without braces)
 * @returns Updated prompt with variable inserted
 */
export function insertVariableIntoPrompt(
  promptContent: string,
  variableName: string
): string {
  const placeholder = `{{${variableName}}}`;

  // If prompt is empty, start with just the variable and a default instruction
  if (!promptContent || promptContent.trim().length === 0) {
    return `${placeholder}\n\nGenerate the document now.`;
  }

  // If prompt is very short, add at the start
  if (promptContent.trim().length < 20) {
    return `${placeholder}\n\n${promptContent}`;
  }

  // Otherwise add at the end with proper spacing
  const trimmed = promptContent.trim();
  return `${trimmed}\n\n${placeholder}`;
}

/**
 * Remove all instances of a variable from a prompt template.
 * Cleans up excess newlines after removal.
 * 
 * @param promptContent - Current prompt content
 * @param variableName - Variable name to remove (without braces)
 * @returns Updated prompt with variable removed
 */
export function removeVariableFromPrompt(
  promptContent: string,
  variableName: string
): string {
  const pattern = new RegExp(`\\{\\{${variableName}\\}\\}`, 'g');
  
  // Remove the variable
  let result = promptContent.replace(pattern, '');
  
  // Clean up excess newlines (max 2 consecutive)
  result = result.replace(/\n{3,}/g, '\n\n');
  
  // Trim and return
  return result.trim();
}

/**
 * Extract all variables used in a prompt.
 * 
 * @param promptContent - Prompt content to analyze
 * @returns Array of variable names (without braces)
 */
export function extractVariablesFromPrompt(promptContent: string): string[] {
  const pattern = /\{\{(\w+)\}\}/g;
  const variables = new Set<string>();
  let match;

  while ((match = pattern.exec(promptContent)) !== null) {
    variables.add(match[1]);
  }

  return Array.from(variables);
}

